import React, { useEffect, useState } from "react";
import Header from "../../directives/header";
import Footer from "../../directives/footer";
import Sidebar from "../../directives/sidebar";
import DataTable, { createTheme } from "react-data-table-component";
import {
  archiveContactUsAction,
  deleteContactUsAction,
  getAllContactRequest,
  getArchivedContactRequest,
} from "../../Action/action";
import { showConfirm } from "../../utilities/alert";
import customStyles from "../CustomStyles";
import moment from "moment";
import { IoCopyOutline } from "react-icons/io5";
import { CopyToClipboard } from "react-copy-to-clipboard";
import toast, { Toaster } from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";

createTheme(
  "darkTheme",
  {
    text: {
      primary: "#FFFFFF",
      secondary: "#9CA3AF",
    },
    background: {
      default: "#0d1635",
    },
    context: {
      background: "#151c33",
      text: "#FFFFFF",
    },
    divider: {
      default: "transparent",
    },
    action: {
      button: "rgba(255,255,255,.7)",
      hover: "rgba(79, 70, 229, 0.2)", // hover color
      disabled: "rgba(255,255,255,.3)",
    },
  },
  "dark",
);

const ContactUs = () => {
  const [searchText, setSearchText] = useState("");
  const [contactUsList, setContactUsList] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [loader, setLoader] = useState(false);
  const [archiveLoader, setArchiveLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");

  const openModal = (title, text) => {
    setModalTitle(title);
    setModalText(text);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTitle("");
    setModalText("");
  };

  useEffect(() => {
    getAllContactUsRequest();
    getArchivedContactUsRequest();
  }, []);
  const getAllContactUsRequest = async () => {
    setLoader(true);
    try {
      const res = await getAllContactRequest();
      if (res?.status) {
        setContactUsList(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res?.msg || "Failed to fetch contact requests");
        setContactUsList([]);
      }
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to fetch contact requests";
      toast.error(message);
      setContactUsList([]);
      console.error("Error fetching contact requests:", error);
    } finally {
      setLoader(false);
    }
  };

  const getArchivedContactUsRequest = async () => {
    setArchiveLoader(true);
    try {
      const res = await getArchivedContactRequest();
      if (res?.status) {
        setArchivedList(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res?.msg || "Failed to fetch archived contacts");
        setArchivedList([]);
      }
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to fetch archived contacts";
      toast.error(message);
      setArchivedList([]);
    } finally {
      setArchiveLoader(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      "Are you sure?",
      "Are you sure you want to delete this?",
    );
    if (!confirmed) {
      return;
    }

    try {
      const res = await deleteContactUsAction({ id });
      if (res?.status) {
        toast.success(res.msg || "Contact inquiry deleted successfully");
        getAllContactUsRequest();
        getArchivedContactUsRequest();
      } else {
        toast.error(res?.msg || "Failed to delete contact request");
      }
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to delete contact request";
      toast.error(message);
    }
  };

  const handleArchiveSelected = async () => {
    if (!selectedRows.length) {
      toast.error("Select at least one contact");
      return;
    }

    const confirmed = await showConfirm(
      "Are you sure?",
      "Are you sure you want to archive this?",
    );
    if (!confirmed) {
      return;
    }

    setArchiving(true);
    let archivedCount = 0;
    let failedCount = 0;

    for (const row of selectedRows) {
      try {
        const res = await archiveContactUsAction({ id: row._id || row.id });
        if (res?.status) {
          archivedCount += 1;
        } else {
          failedCount += 1;
        }
      } catch {
        failedCount += 1;
      }
    }

    setClearSelectedRows((prev) => !prev);
    setSelectedRows([]);
    await Promise.all([
      getAllContactUsRequest(),
      getArchivedContactUsRequest(),
    ]);
    setArchiving(false);

    if (archivedCount && !failedCount) {
      toast.success(
        archivedCount === 1
          ? "Contact inquiry archived successfully"
          : `${archivedCount} contacts archived successfully`,
      );
    } else if (archivedCount && failedCount) {
      toast.error(`${archivedCount} archived, ${failedCount} failed`);
    } else {
      toast.error("Failed to archive contact request");
    }
  };

  const handleDownload = async () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        toast.error("No data to export");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Contact Requests");

      // ✅ Columns
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 10 },
        { header: "Name", key: "name", width: 25 },
        { header: "Email", key: "email", width: 35 },
        { header: "Organisation", key: "organisation", width: 28 },
        { header: "Inquiry Type", key: "inquiryType", width: 30 },
        { header: "Subject", key: "subject", width: 30 },
        { header: "Message", key: "message", width: 50 },
        { header: "Date", key: "date", width: 20 },
      ];

      filteredData.forEach((item, index) => {
        worksheet.addRow({
          sno: index + 1,
          name: item.name || "",
          email: item.email || "",
          organisation: item.organisation || "",
          inquiryType: item.inquiryType || "",
          subject: item.subject || "",
          message: item.message || "",
          date: item.createdAt ? moment(item.createdAt).format("DD/MM/YY") : "",
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };

      worksheet.getColumn("sno").alignment = { horizontal: "center" };
      worksheet.getColumn("date").alignment = { horizontal: "center" };

      worksheet.getColumn("message").alignment = { wrapText: true };

      worksheet.autoFilter = {
        from: "A1",
        to: "H1",
      };

      // ✅ Generate file
      const buffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "Contact_Requests.xlsx",
      );
    } catch (error) {
      console.error("Excel download error:", error);
      toast.error("Failed to download Excel");
    }
  };
  const copieBtn = async () => {
    toast.success(`Copied!`);
  };
  const columns = [
    {
      name: "S.No",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "100px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      exportValue: (row) => row.email, // 👈 add this

      sortable: true,
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{row?.email}</span>
          <CopyToClipboard text={row?.email}>
            <span
              title="Click to Copy"
              className="mr-copylink"
              id="token-buy-button"
              onClick={copieBtn}
              style={{ cursor: "pointer", color: "#ea5536", marginLeft: 8 }}
            >
              <IoCopyOutline />
            </span>
          </CopyToClipboard>
        </div>
      ),
      // width: '210px',
    },
    {
      name: "Organisation",
      selector: (row) => row.organisation || "—",
      sortable: true,
    },
    {
      name: "Inquiry Type",
      selector: (row) => row.inquiryType || "—",
      sortable: true,
    },
    {
      name: "Subject",
      cell: (row) => {
        const text = row?.subject || "";
        const shouldTruncate = text && text.length > 7;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{shouldTruncate ? `${text.slice(0, 7)}...` : text}</span>
            {shouldTruncate && (
              <span
                onClick={() => openModal("Subject", text)}
                style={{ cursor: "pointer", color: "#ea5536", marginLeft: 8 }}
                title={"View full subject"}
              >
                {"View"}
              </span>
            )}
          </div>
        );
      },
    },

    {
      name: "Message",
      cell: (row) => {
        const text = row?.message || "";
        const shouldTruncate = text && text.length > 7;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{shouldTruncate ? `${text.slice(0, 7)}...` : text}</span>
            {shouldTruncate && (
              <span
                onClick={() => openModal("Message", text)}
                style={{ cursor: "pointer", color: "#ea5536", marginLeft: 8 }}
                title={"View full message"}
              >
                {" View"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      name: "Date",
      selector: (row) => moment(row.createdAt).format("DD/MM/YY"),
      center: true,
      width: "200px",
    },
    {
      name: "Action",
      center: true,
      width: "100px",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-danger"
          title="Delete"
          onClick={() => handleDelete(row._id || row.id)}
        >
          <i className="fa fa-trash" />
        </button>
      ),
    },
  ];

  const matchesSearch = (item) =>
    ["name", "email", "organisation", "inquiryType", "subject", "message"].some(
      (key) => item[key]?.toLowerCase().includes(searchText.toLowerCase()),
    );

  const filteredData = contactUsList?.filter(matchesSearch);
  const filteredArchive = archivedList?.filter(matchesSearch);

  return (
    <>
      <div className="wrapper h-auto" style={{ height: "100vh" }}>
        <Header />
        <Sidebar />
        <Toaster />

        <div
          className="content-wrapper"
          style={{ height: "100vh", overflowY: "scroll" }}
        >
          <div className="container-full">
            <section className="content">
              <div className="row">
                <div className="col-lg-12 col-12">
                  <div className="box">
                    <div className="box-header with-border d-md-flex justify-content-between align-items-center">
                      <h3 className="box-title" style={{ fontWeight: 600 }}>
                        Contact Us Request
                      </h3>
                      <div className="d-flex gap-3 justify-content-end">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search..."
                          style={{ width: "250px" }}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={handleArchiveSelected}
                          className="btn btn-primary"
                          disabled={archiving || selectedRows.length === 0}
                        >
                          {archiving ? "Archiving..." : "Archive"}
                        </button>
                        <button
                          onClick={handleDownload}
                          className="btn btn-primary "
                        >
                          <Download />
                        </button>
                      </div>
                    </div>

                    <div className="box-body">
                      {!loader ? (
                        <div className="table-responsive contact-us-table">
                          <DataTable
                            columns={columns}
                            data={filteredData}
                            pagination
                            striped
                            highlightOnHover
                            theme="darkTheme"
                            customStyles={customStyles}
                            selectableRows
                            selectableRowsHighlight
                            onSelectedRowsChange={({ selectedRows: rows }) =>
                              setSelectedRows(rows)
                            }
                            clearSelectedRows={clearSelectedRows}
                          />
                        </div>
                      ) : (
                        <>
                          <br />
                          <br />
                          <center>
                            <h4>
                              <i className="fa fa-spinner fa-spin"></i> &nbsp;
                              Please wait
                            </h4>
                          </center>
                          <br />
                          <br />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="box">
                    <div className="box-header with-border">
                      <h3 className="box-title" style={{ fontWeight: 600 }}>
                        Archive
                      </h3>
                    </div>
                    <div className="box-body">
                      {!archiveLoader ? (
                        <div className="table-responsive">
                          <DataTable
                            columns={columns}
                            data={filteredArchive}
                            pagination
                            striped
                            highlightOnHover
                            theme="darkTheme"
                            customStyles={customStyles}
                            noDataComponent={
                              <div style={{ padding: 24, color: "#fff" }}>
                                No archived contacts
                              </div>
                            }
                          />
                        </div>
                      ) : (
                        <>
                          <br />
                          <br />
                          <center>
                            <h4>
                              <i className="fa fa-spinner fa-spin"></i> &nbsp;
                              Please wait
                            </h4>
                          </center>
                          <br />
                          <br />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
        {/* Modal popup for full Subject/Message */}
        {modalOpen && (
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#081028",
                padding: 20,
                borderRadius: 8,
                maxWidth: 800,
                width: "30%",
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: 12,
                }}
              >
                <h4 style={{ margin: 0 }}>{modalTitle}</h4>
                <button
                  onClick={closeModal}
                  style={{
                    background: "transparent",
                    border: "none",
                    fontSize: 20,
                    color: "#FFF",
                    cursor: "pointer",
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {modalText}
              </div>
            </div>
          </div>
        )}
        <Footer />
      </div>
    </>
  );
};

export default ContactUs;
